import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService } from "../dataMapperServices";
import { DATA_MAPPER_ROUTE, routeForStep } from "../shared/editorRoutes";
import {
  isRecordChanged,
  sameId,
  type DataMapperProfile,
  type DataMapperProfileCreate,
  type DataMapperProfilePatch,
} from "../dataMapper.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** Where a profile opens when it was not left on a later step: the wizard's Source step. */
export const DATA_MAPPER_FIRST_STEP_ROUTE = DATA_MAPPER_ROUTE.source;

export interface DataMapperProfileFormData {
  name: string;
  description: string;
  titanSource: boolean;
  /** The client company a NEW profile is for (chosen in the form); an edit keeps the profile's own. */
  clientId: string | null;
}

/** How a save ended. `conflict` = 409 record-changed: the form closes and the list reloads. */
export type ProfileSaveOutcome = "saved" | "conflict" | "failed";

export interface ClientOption {
  id: string;
  name: string;
  code?: string;
  parentClientId?: string | null;
}

/** A client company in the form's picker: a top-level company, or a child company under it. */
export interface CompanyOption extends ClientOption {
  /** The top-level company it is listed under (its own name for a top-level one). */
  group: string;
  /** The parent's name for a child company, null for a top-level one. */
  parentName: string | null;
}

/** How a profile's client company reads in the list. */
export interface ProfileCompany {
  name: string;
  code: string | null;
  /** The parent company, when the profile's client is a child company. */
  parentName: string | null;
}

const EMPTY_FORM: DataMapperProfileFormData = { name: "", description: "", titanSource: false, clientId: null };

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** The sentence a 409 record-changed shows above the list. */
export const recordChangedNotice = (name: string): string =>
  `Someone else saved "${name}" while you were editing it, so your changes were not saved. ` +
  "The list has been reloaded with their version; edit the profile again to apply yours.";

/**
 * Data Mapper → Profiles: every Data Mapper profile, by client company; create /
 * edit / delete them, and open one.
 *
 * There is no "choose a client first" (item #3983, the 2026-10-22 onboarding):
 * the list shows every profile, a profile is opened straight from it (which
 * selects its client), and a NEW profile's client company is chosen in its
 * form. The header's client filter is optional and lists top-level companies
 * only (item #3982): a group's child companies are not listed beside it, and
 * choosing the group shows its own profiles and its child companies'. Profiles
 * are per client company (a child company has its own), by the onboarding's
 * decision that plants share one only when their Titan setup matches.
 *
 * Mirrors DevOpsConnectionsStore: a plain MobX class (no ViewStore base), the
 * list is a COMPUTED view over the `data-mapper-profiles` ServiceStore's cache,
 * and the create/edit form state lives here. The selected client and the opened
 * profile are NOT held here — they are ids on DataMapperViewStore (plan §2.4),
 * which every Data Mapper screen shares.
 */
class DataMapperProfilesStore {
  rootStore: RootStore;

  loading = false;
  /** True once a load for the current client has finished (success or failure). */
  loaded = false;
  /** The load failure, shown in the empty list. */
  error: string | null = null;
  /** A failed row action (open / delete), shown above the list. */
  actionError: string | null = null;
  /** A non-error sentence above the list — the 409 record-changed explanation. */
  notice: string | null = null;

  searchQuery = "";
  /** The header's optional filter: a top-level company (its child companies included), or all. */
  clientFilterId: string | null = null;

  // Create / edit form
  editingProfileId: string | null = null;
  /** The `updatedAt` the edit form was loaded from — sent back as `expectUpdatedAt`. */
  editingUpdatedAt: string | null = null;
  formData: DataMapperProfileFormData = { ...EMPTY_FORM };
  saving = false;
  formError: string | null = null;

  /** Bumped per load so a slow response for a previous client cannot clear a newer load's state. */
  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service() {
    return dataMapperService(this.rootStore, "data-mapper-profiles");
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  /** HQ's client list (the app-wide ClientStore over the `clients` ServiceStore). */
  get clients(): ClientOption[] {
    const clientStore = (this.rootStore as unknown as { clientStore?: { clients?: ClientOption[] } })
      .clientStore;
    return clientStore?.clients ?? [];
  }

  private clientById(id: string | null | undefined): ClientOption | null {
    if (!id) return null;
    return this.clients.find((c) => sameId(c.id, id)) ?? null;
  }

  /**
   * The companies the header lists: those with no parent. shortcut: a child whose parent is not
   * in the loaded list (ClientStore reads 200) is listed at the top level, so it stays reachable;
   * read the parents by id if HQ ever holds more clients than that.
   */
  get topLevelClients(): ClientOption[] {
    return this.clients.filter((c) => !c.parentClientId || !this.clientById(c.parentClientId)).sort(byName);
  }

  /** A top-level company's child companies, by name. */
  childCompanies(parentId: string): ClientOption[] {
    return this.clients.filter((c) => c.parentClientId && sameId(c.parentClientId, parentId)).sort(byName);
  }

  /**
   * The form's picker: each top-level company, then its child companies, grouped under it.
   * shortcut: built from ClientStore's cache, which reads the first 200 clients by name (as the old
   * header picker did), so a client past that cannot be chosen; search the `clients` service as the
   * person types once HQ holds more than 200 (Codex r1).
   */
  get companyOptions(): CompanyOption[] {
    return this.topLevelClients.flatMap((top) => [
      { ...top, group: top.name, parentName: null },
      ...this.childCompanies(top.id).map((child) => ({ ...child, group: top.name, parentName: top.name })),
    ]);
  }

  /** The header's filter, as the option it shows. */
  get clientFilter(): ClientOption | null {
    return this.clientById(this.clientFilterId);
  }

  /** The top-level company a client belongs to (itself when it has no parent). */
  private topOf(clientId: string | null | undefined, parentId?: string | null): string | null {
    if (!clientId) return null;
    const parent = parentId ?? this.clientById(clientId)?.parentClientId ?? null;
    return parent ?? clientId;
  }

  /** How a profile's client company reads in the list: the API's names, else HQ's client list. */
  companyOf(profile: DataMapperProfile): ProfileCompany {
    const client = this.clientById(profile.clientId);
    const parentId = profile.clientParentId ?? client?.parentClientId ?? null;
    return {
      name: profile.clientName ?? client?.name ?? "Unknown client",
      code: profile.clientCode ?? client?.code ?? null,
      parentName: parentId ? (profile.clientParentName ?? this.clientById(parentId)?.name ?? null) : null,
    };
  }

  /**
   * Every profile, a group's own first and then its child companies', each by name: a computed
   * view over the ServiceStore cache. The filter keeps one top-level company and its children.
   */
  get profiles(): DataMapperProfile[] {
    const all = (this.service?.entities ?? []) as unknown as DataMapperProfile[];
    const filter = this.clientFilterId;
    const keyed = all.map((p) => {
      const company = this.companyOf(p);
      return { p, company, top: this.topOf(p.clientId, p.clientParentId) };
    });
    return keyed
      .filter((k) => !filter || sameId(k.top, filter))
      .sort(
        (a, b) =>
          (a.company.parentName ?? a.company.name).localeCompare(b.company.parentName ?? b.company.name, undefined, {
            sensitivity: "base",
          }) ||
          Number(a.company.parentName !== null) - Number(b.company.parentName !== null) ||
          byName(a.company, b.company) ||
          byName(a.p, b.p)
      )
      .map((k) => k.p);
  }

  get filteredProfiles(): DataMapperProfile[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.profiles;
    return this.profiles.filter((p) => {
      const company = this.companyOf(p);
      return [p.name, p.description, company.name, company.code, company.parentName].some((text) =>
        (text ?? "").toLowerCase().includes(q)
      );
    });
  }

  /** Show one top-level company (and its child companies), or every profile. Nothing is selected. */
  setClientFilter(clientId: string | null) {
    this.clientFilterId = clientId || null;
    this.actionError = null;
  }

  /** The client company the form is for: chosen there (a new profile), else the profile's own. */
  get formCompany(): ClientOption | null {
    return this.clientById(this.formData.clientId);
  }

  profileById(id: string | null): DataMapperProfile | null {
    if (!id) return null;
    return this.profiles.find((p) => sameId(p.id, id)) ?? null;
  }

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  clearActionError() {
    this.actionError = null;
  }

  clearNotice() {
    this.notice = null;
  }

  /** Load HQ's clients for the picker when nothing has loaded them yet. */
  async ensureClients(): Promise<void> {
    const clientStore = (
      this.rootStore as unknown as {
        clientStore?: { clients?: unknown[]; fetchClients?: () => Promise<void> };
      }
    ).clientStore;
    if (clientStore && !(clientStore.clients && clientStore.clients.length)) {
      await clientStore.fetchClients?.();
    }
  }

  /** HQ's client list failed to load (the picker is then empty for a reason). */
  get clientsError(): string | null {
    const clientStore = (this.rootStore as unknown as { clientStore?: { error?: string | null } })
      .clientStore;
    return clientStore?.error ?? null;
  }

  /**
   * Route entry, one awaited sequence (Codex r1): bind the selection to the
   * signed-in user, load HQ's clients, drop a remembered client HQ no longer
   * has (New Profile would then default to it and be refused), then list every
   * profile.
   */
  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    if (!this.canUse) return;
    await this.ensureClients();
    await this.dropMissingClient();
    await this.load();
  }

  /**
   * The remembered client is not in the loaded list: ask for it by id (the list
   * is capped), and forget it only when HQ answers 404. Any other failure keeps
   * it; the profiles load then reports what is wrong.
   */
  private async dropMissingClient(): Promise<void> {
    const clientId = this.clientId;
    if (!clientId || this.clientById(clientId) || this.clientsError) return;
    const root = this.rootStore as unknown as {
      serviceStoreManager?: { getStore: (p: string) => unknown } | null;
      clientsStore?: unknown;
    };
    const clients = (root.serviceStoreManager?.getStore("clients") ?? root.clientsStore) as
      | { get?: (id: string, params?: Record<string, unknown>) => Promise<unknown> }
      | null
      | undefined;
    try {
      await clients?.get?.(clientId, { throwOnError: true });
    } catch (err) {
      if (isSessionEnded(err)) return;
      if ((err as { code?: unknown } | null)?.code === 404) this.selection?.selectClient(null);
    }
  }

  /** Every profile, of every client. Called from the route's onEnter and after every write. */
  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    if (!this.canUse) {
      this.loading = false;
      this.loaded = false;
      this.error = null;
      return;
    }
    this.loading = true;
    this.error = null;
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // find ended in between would still pass the seq check and latch `loaded`.
    const ended = sessionMark(this.rootStore);
    try {
      // The store is `throwOnError: true` (dataMapper.stores.ts), so a refusal
      // rejects here, and a null result is a find superseded by a newer one
      // ("latest-wins") — not a failure. Not findOrThrow: it reads the store's
      // shared errorOnFind after the await, which a superseded request's
      // failure can set under a newer, successful load (Codex r1).
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      await this.service.find({ query: {} });
    } catch (err) {
      runInAction(() => {
        if (seq === this.loadSeq) { if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "The profiles could not be loaded."); }
      });
    } finally {
      runInAction(() => {
        if (seq === this.loadSeq && !ended()) {
          this.loading = false;
          this.loaded = true;
        }
      });
    }
  }

  // ---- Create / edit form -------------------------------------------------

  /**
   * A new profile. Its client company starts as the header's filter, else the client of the
   * profile that is open in this tab; the person confirms or changes it in the form.
   */
  startCreate() {
    this.editingProfileId = null;
    this.editingUpdatedAt = null;
    this.formError = null;
    const start = this.clientFilterId ?? this.clientId;
    this.formData = { ...EMPTY_FORM, clientId: this.clientById(start) ? start : null };
  }

  startEdit(profile: DataMapperProfile) {
    this.editingProfileId = profile.id;
    this.editingUpdatedAt = profile.updatedAt;
    this.formError = null;
    this.formData = {
      name: profile.name,
      description: profile.description ?? "",
      titanSource: profile.titanSource === true,
      clientId: profile.clientId,
    };
  }

  get isEditing(): boolean {
    return this.editingProfileId !== null;
  }

  setFormField<K extends keyof DataMapperProfileFormData>(field: K, value: DataMapperProfileFormData[K]) {
    this.formData = { ...this.formData, [field]: value };
  }

  get isFormValid(): boolean {
    return this.formData.name.trim().length > 0 && (this.isEditing || this.formData.clientId !== null);
  }

  /**
   * Create or edit. An edit sends back the `updatedAt` it was loaded from, so a
   * concurrent save is a 409 `record-changed` instead of a silent overwrite.
   * On that 409 the form is closed (its base is stale), the list reloads with
   * the other person's version, and a sentence says what happened.
   */
  async saveProfile(): Promise<ProfileSaveOutcome> {
    if (!this.isFormValid) {
      this.formError = this.formData.name.trim()
        ? "Choose the client company this profile is for."
        : "A profile needs a name.";
      return "failed";
    }
    const service = this.service;
    if (!service) {
      this.formError = "The Data Mapper service is not available in this build.";
      return "failed";
    }
    const name = this.formData.name.trim();
    const description = this.formData.description.trim() || null;
    const titanSource = this.formData.titanSource;
    const editingId = this.editingProfileId;
    this.saving = true;
    this.formError = null;
    try {
      if (editingId) {
        const patch: DataMapperProfilePatch = { name, description, titanSource };
        if (this.editingUpdatedAt) patch.expectUpdatedAt = this.editingUpdatedAt;
        await service.patch(editingId, patch as never);
      } else {
        const clientId = this.formData.clientId as string;
        const data: DataMapperProfileCreate = { clientId, name, description, titanSource };
        await service.create(data as never);
        // A new profile for a company the filter hides would seem to vanish: show every profile.
        if (this.clientFilterId && !sameId(this.topOf(clientId), this.clientFilterId)) this.clientFilterId = null;
      }
      runInAction(() => {
        this.editingProfileId = null;
        this.editingUpdatedAt = null;
        this.formData = { ...EMPTY_FORM };
        this.notice = null;
      });
      // Reload: a create/patch answer carries `state` and no `stateSummary`,
      // so the list's summary comes back only from `find`.
      await this.load();
      return "saved";
    } catch (err) {
      if (isSessionEnded(err)) return "failed";
      if (isRecordChanged(err)) {
        runInAction(() => {
          this.notice = recordChangedNotice(name);
          this.editingProfileId = null;
          this.editingUpdatedAt = null;
          this.formData = { ...EMPTY_FORM };
        });
        await this.load();
        return "conflict";
      }
      runInAction(() => {
        this.formError = loadErrorMessage(err, "The profile could not be saved.");
      });
      return "failed";
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  // ---- Row actions --------------------------------------------------------

  /**
   * Delete a profile and everything it owns (its schemas, mapping configs and
   * lookup tables — the API removes them in one transaction). The caller MUST
   * confirm first (RowActionsMenu `confirm`). If it was the open profile, the
   * selection drops it.
   */
  async removeProfile(id: string): Promise<boolean> {
    this.actionError = null;
    this.notice = null;
    try {
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      await this.service.remove(id);
      this.selection?.forgetProfile(id);
      await this.load();
      return true;
    } catch (err) {
      // Ended by a sign-out: no error, and no re-read in the next session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = loadErrorMessage(err, "The profile could not be deleted.");
      });
      await this.load();
      return false;
    }
  }

  /**
   * Open a profile: stamp `touch: true` (its "last opened" time, which the
   * standalone tool wrote on set-active), put its id on the selection, and go
   * to the Library. The stamp is not a save, so it sends no expectUpdatedAt.
   * A refusal (deleted meanwhile, not operator) is shown, and nothing is opened.
   */
  async openProfile(profile: DataMapperProfile): Promise<boolean> {
    this.actionError = null;
    this.notice = null;
    try {
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      const touch: DataMapperProfilePatch = { touch: true };
      await this.service.patch(profile.id, touch as never);
      this.selection?.selectProfile(profile.clientId, profile.id);
      // Opening (or re-opening) a profile reads its saved progress afresh (Codex r1).
      this.forgetSavedProgressRead();
      // Resume on the step it was left on (its saved lastStep), else the wizard's Source step.
      void this.rootStore.routerStore?.goTo(routeForStep(profile.stateSummary?.lastStep ?? profile.lastStep));
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = loadErrorMessage(err, `"${profile.name}" could not be opened.`);
      });
      await this.load();
      return false;
    }
  }

  /** The editors read the open profile's saved progress again on their next entry. */
  private forgetSavedProgressRead() {
    (
      this.rootStore as unknown as { dataMapperProfileStateViewStore?: { reset: () => void } }
    ).dataMapperProfileStateViewStore?.reset();
  }

  /** Route exit: drop the transient form and messages (the selection and the client filter stay). */
  reset() {
    this.loadSeq++;
    this.loading = false;
    this.actionError = null;
    this.notice = null;
    this.formError = null;
    this.editingProfileId = null;
    this.editingUpdatedAt = null;
    this.formData = { ...EMPTY_FORM };
    this.searchQuery = "";
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.loaded = false;
    this.error = null;
    this.saving = false;
    this.clientFilterId = null;
  }
}

export default DataMapperProfilesStore;
